import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  DatePicker,
  Drawer,
  Form,
  Row,
} from 'antd';
import moment from 'moment';
import { DateTime } from 'luxon';

import DisplayText from '../common/text/DisplayText';
import FormFileInput from '../common/inputs/FormFileInput';
import FormTextInput from '../common/inputs/FormTextInput';
import CertificationTypeInput from './CertificationTypeInput';
import DrawerSubmitFooter from '../common/containers/DrawerSubmitFooter';
import { getIdMap, isNullOrUndefined } from '../helpers/helpers';
import CustomFields from '../common/customFields/CustomFields';
import { formatCustomFieldCreatePayload } from '../helpers/costcodeHelpers';
import { canManageCertifications } from '../users/userHelpers';

import {
  getCertification,
  createCertification,
  updateCertification,
  getCertificationCustomData,
  getCertificationTypes,
} from './state/certifications.actions';
import { calculateCertificationExpiryDate } from './certificationExpiryHelpers';

const formatTimestamp = (timestamp) => {
  if (!timestamp) return null;
  return DateTime.fromSeconds(timestamp).toLocaleString(DateTime.DATE_MED);
};

export default function AddEditCertification(props) {
  const dispatch = useDispatch();
  const {
    id,
    certificationId,
    visible,
    onClose,
    certification,
    onDeleteClick,
    entityType,
    isDisplay,
  } = props;

  const {
    [entityType]: {
      selectedCertification = {},
      certificationCustomData,
      certificationCustomDataFiles,
      certificationCustomFieldTemplate,
      certificationTypes = [],
    } = {},
  } = useSelector((state) => state.certifications);
  const users = useSelector((state) => state.users.users);
  const userDivisions = useSelector((state) => state.users.userDivisions);
  const vendors = useSelector((state) => state.vendors.vendors);
  const equipmentList = useSelector((state) => state.equipment.equipment);
  const equipmentMap = useMemo(() => getIdMap(equipmentList), [equipmentList]);
  const userMap = useMemo(() => getIdMap(users), [users]);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [customCertificationTypes, setCustomCertificationTypes] = useState([]);
  const [shouldUpdate, forceUpdate] = useReducer((x) => x + 1, 0);
  const [prevVisible, setPrevVisible] = useState(visible);
  const expiryManuallyTouched = useRef(false);

  const canAddNew = useMemo(() => {
    if (isDisplay) return false;
    return canManageCertifications(entityType, userMap[id]);
  }, [entityType, isDisplay, userMap, id]);

  const certificationTypeMap = useMemo(
    () => certificationTypes.reduce((acc, ct) => {
      acc[ct.id] = ct;
      return acc;
    }, {}),
    [certificationTypes],
  );

  const certificationTypeIdSet = useMemo(
    () => new Set(certificationTypes.map((ct) => ct.id)),
    [certificationTypes],
  );

  const certificationTypeNameSet = useMemo(
    () => new Set(certificationTypes.map((ct) => ct.name)),
    [certificationTypes],
  );

  const certificationTypeOptions = useMemo(() => (
    certificationTypes
      .map((ct) => ({ id: ct.id, title: ct.name }))
      .concat(customCertificationTypes)
  ), [certificationTypes, customCertificationTypes]);

  const onCertificationTypeAddNew = useCallback((newType) => {
    if (!certificationTypeNameSet.has(newType)) {
      setCustomCertificationTypes((prev) => prev.concat([{ id: newType, title: newType }]));
    }
  }, [certificationTypeNameSet]);

  const relevantDivisions = useMemo(() => {
    switch (entityType) {
      case 'users': return userDivisions?.[id] ?? [];
      case 'vendors': return vendors?.[id]?.divisionIds ?? [];
      case 'equipment': return equipmentMap[id]?.divisionIds ?? [];
      default: return [];
    }
  }, [entityType, userDivisions, vendors, equipmentMap, id]);

  useEffect(() => {
    forceUpdate();
  }, [certificationCustomData]);

  useEffect(() => {
    dispatch(getCertificationTypes(entityType));
  }, [entityType]);

  useEffect(() => {
    if (id && certificationId) {
      dispatch(getCertification(entityType, id, certificationId));
    }

    dispatch(getCertificationCustomData(entityType, certificationId));
  }, [entityType, id, certificationId]);

  useEffect(() => {
    setCustomFields(certificationCustomFieldTemplate?.sections);
  }, [certificationCustomFieldTemplate]);

  useEffect(() => {
    if (form) {
      const relevantCertification = selectedCertification || certification;
      form.resetFields();

      if (relevantCertification && certificationId) {
        form.setFieldsValue({
          typeId: relevantCertification.typeId,
          acquiredDate: relevantCertification.acquiredDate
            ? moment.unix(relevantCertification.acquiredDate)
            : null,
          expiryDate: relevantCertification.expiryDate
            ? moment.unix(relevantCertification.expiryDate)
            : null,
          issuer: relevantCertification.issuer,
        });
        setFiles(relevantCertification.files ?? []);
      } else {
        setFiles([]);
        setErrors({});
      }
    }
  }, [form, selectedCertification, certification, certificationId]);

  const updateAutoExpiry = (typeId, acquiredDate) => {
    if (
      expiryManuallyTouched.current
      || !acquiredDate
      || !typeId
    ) {
      return;
    }

    const selectedType = certificationTypeMap[typeId];
    if (!selectedType) return;

    const expirySeconds = calculateCertificationExpiryDate({
      issueDateMs: acquiredDate.valueOf(),
      renewalPeriod: selectedType.renewalPeriod,
      renewalPeriodUnit: selectedType.renewalPeriodUnit,
    });

    form.setFieldsValue({
      expiryDate: expirySeconds ? moment.unix(expirySeconds) : null,
    });
  };

  const onCertificationTypeChange = (newTypeId) => {
    expiryManuallyTouched.current = false;
    const formValues = form.getFieldsValue();
    updateAutoExpiry(newTypeId, formValues.acquiredDate);
    form.setFieldsValue({
      typeId: newTypeId,
    });
  };

  const onAcquiredDateChange = (newAcquiredDate) => {
    const formValues = form.getFieldsValue();
    updateAutoExpiry(formValues.typeId, newAcquiredDate);
    form.setFieldsValue({
      acquiredDate: newAcquiredDate,
    });
  };

  const onExpiryDateChange = (newExpiryDate) => {
    expiryManuallyTouched.current = true;
    form.setFieldsValue({
      expiryDate: newExpiryDate,
    });
  };

  const onFormSubmit = useCallback(async () => {
    let payload;
    try {
      payload = { ...(await form.validateFields()) };
    } catch {
      return;
    }

    setLoading(true);

    // Normalize data for backend
    Object.keys(payload).forEach((key) => {
      if (isNullOrUndefined(payload[key])) {
        payload[key] = null;
      } else if (key === 'acquiredDate' || key === 'expiryDate') {
        payload[key] = payload[key].unix();
      }
    });

    if (payload.typeId && !certificationTypeIdSet.has(payload.typeId)) {
      payload.type = payload.typeId;
      delete payload.typeId;
    }

    const formattedValues = formatCustomFieldCreatePayload({
      payload,
      customFields,
    });

    if (formattedValues?.error) {
      setErrors(formattedValues.errorMap);
      setLoading(false);
      return;
    }

    setErrors({});
    const result = await (certificationId
      ? dispatch(updateCertification(entityType, id, certificationId, formattedValues, files))
      : dispatch(createCertification(entityType, id, formattedValues, files)));

    setLoading(false);
    if (result) {
      setFiles([]);
      setErrors({});
      onClose();
    }
  }, [
    certificationId,
    certificationTypeIdSet,
    customFields,
    entityType,
    files,
    form,
    id,
    onClose,
  ]);

  const onDrawerClose = useCallback(() => {
    setFiles([]);
    setErrors({});
    onClose();
  }, [setFiles, onClose]);

  const fullValidIds = useMemo(
    () => (
      new Set(
        Array.from(certificationTypeIdSet).concat(
          customCertificationTypes.map((ct) => ct.id),
        ),
      )
    ),
    [certificationTypeIdSet, customCertificationTypes],
  );

  if (visible !== prevVisible) {
    setPrevVisible(visible);
    setCustomCertificationTypes([]);
    if (visible && !certificationId) {
      expiryManuallyTouched.current = false;
    }
  }

  let title = `${certificationId ? 'Edit' : 'Add'} Certification`;
  if (isDisplay) {
    title = 'View Certification';
  }

  return (
    <Drawer
      title={title}
      visible={visible}
      onClose={onDrawerClose}
      width={800}
      maskClosable={false}
      push={false}
    >
      <Form
        layout="vertical"
        form={form}
      >
        <Form.Item
          name="typeId"
          label="Type"
          rules={[{ required: true, message: 'Please select a certification type' }]}
          style={{ marginBottom: 20 }}
        >
          {isDisplay
            ? <DisplayText title={certification?.type} />
            : (
              <CertificationTypeInput
                options={certificationTypeOptions}
                validIds={fullValidIds}
                onAddNew={onCertificationTypeAddNew}
                onChange={onCertificationTypeChange}
                canAddNew={canAddNew}
              />
            )}
        </Form.Item>
        <Form.Item
          name="acquiredDate"
          label="Acquired Date"
        >
          {
            isDisplay
              ? (
                <DisplayText
                  title={formatTimestamp(certification?.acquiredDate)}
                />
              )
              : (
                <DatePicker
                  disabledDate={(d) => !d || (form && d.isAfter(
                    form.getFieldValue('expiryDate') || null,
                  ))}
                  onChange={onAcquiredDateChange}
                />
              )
          }
        </Form.Item>
        <Form.Item
          name="expiryDate"
          label="Expiry Date"
        >
          {
            isDisplay
              ? (
                <DisplayText
                  title={formatTimestamp(certification?.expiryDate)}
                />
              )
              : (
                <DatePicker
                  disabledDate={(d) => !d || (form && d.isBefore(
                    form.getFieldValue('acquiredDate') || null,
                  ))}
                  onChange={onExpiryDateChange}
                />
              )
          }
        </Form.Item>
        <FormTextInput
          isNotDisplay={!isDisplay}
          name="issuer"
          label="Issuer"
          value={certification?.issuer}
          style={{ marginBottom: 20 }}
        />
        <Row className="user-add-certification-form-label">
          Files
        </Row>
        <FormFileInput
          files={files}
          setFiles={setFiles}
          useIconForButton={false}
          isReadOnly={isDisplay}
        />
        { !!customFields?.length && (
          <Form.Item
            name="customData"
            style={{ marginTop: 20, marginBottm: 20, width: '100%' }}
          >
            <CustomFields
              key={`${certificationId}${shouldUpdate}`} // Need to force re-render
              initialValues={certificationCustomData}
              customFields={customFields}
              divisions={relevantDivisions}
              errors={errors}
              fileMap={certificationCustomDataFiles}
              isDisplay={isDisplay}
            />
          </Form.Item>
        )}
      </Form>
      <DrawerSubmitFooter
        onClose={onDrawerClose}
        onDelete={(!isDisplay && certificationId)
          ? () => onDeleteClick(certification)
          : undefined}
        onSubmit={isDisplay ? undefined : onFormSubmit}
        loading={loading}
      />
    </Drawer>
  );
}

AddEditCertification.propTypes = {
  id: PropTypes.string.isRequired,
  certificationId: PropTypes.string,
  certification: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    acquiredDate: PropTypes.number,
    expiryDate: PropTypes.number,
    issuer: PropTypes.string,
    typeId: PropTypes.string,
  }),
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  onDeleteClick: PropTypes.func,
  entityType: PropTypes.string.isRequired,
  isDisplay: PropTypes.bool,
};

AddEditCertification.defaultProps = {
  certificationId: null,
  certification: {},
  visible: false,
  onClose: () => {},
  onDeleteClick: () => {},
  isDisplay: false,
};
